import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Shield, Clock, Award, Users, ChevronRight, CheckCircle, Sun, Moon } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import { CMSProvider, useCMS } from '../context/CMSContext';
import { ServicesSection } from '../components/home/ServicesSection';
import { FleetSection } from '../components/home/FleetSection';
import { CoverageSection } from '../components/home/CoverageSection';
import { ContactTeamSection } from '../components/home/ContactTeamSection';
import { StatsSection } from '../components/home/StatsSection';
import { ProcessSection } from '../components/home/ProcessSection';
import { WhyChooseUsSection } from '../components/home/WhyChooseUsSection';
import { HomeFooter } from '../components/home/HomeFooter';
import { QuoteModal, SuccessModal } from '../components/home/QuoteModal';
import { LogisticsGlobe3D } from '../components/home/LogisticsGlobe3D';
import { AnimatedRouteSection } from '../components/home/AnimatedRouteSection';
import { PublicTrackingResult } from '../components/customer/ShipmentTrackingMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_c8eaf99c-4f2a-48e9-9fc0-504e56c7d156/artifacts/yqk618vz_WhatsApp_Image_2025-10-17_at_11.26.30-removebg-preview.png';
const HERO_IMAGE = 'https://images.unsplash.com/photo-1774013603273-03507c48a0e8?crop=entropy&cs=srgb&fm=jpg&q=85';

const INITIAL_QUOTE_FORM = {
  name: '', email: '', phone: '',
  pickup_pincode: '', delivery_pincode: '',
  pickup_city: '', delivery_city: '',
  pickup_state: '', delivery_state: '',
  transport_type: 'FTL', vehicle_type: 'Container',
  total_weight: '', material_type: '', description: ''
};

const useSafeNavigate = () => { try { return useNavigate(); } catch { return () => {}; } };

const HomePagePremium = ({ portalUrl }) => {
  const navigate = useSafeNavigate();
  const { theme, toggleTheme } = useTheme();
  const cms = useCMS();
  const goTo = (path) => portalUrl ? (window.location.href = `${portalUrl}${path}`) : navigate(path);

  React.useEffect(() => { document.title = 'Tridev Logistics | Trusted FTL & PTL Transport Services Across India'; }, []);
  const [trackingId, setTrackingId] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [trackingError, setTrackingError] = useState('');
  const [isTracking, setIsTracking] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteForm, setQuoteForm] = useState(INITIAL_QUOTE_FORM);
  const [pickupValid, setPickupValid] = useState(null);
  const [deliveryValid, setDeliveryValid] = useState(null);

  const fetchPincode = async (pincode, type) => {
    if (pincode.length === 6) {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/pincode/${pincode}`);
        if (data.city && data.city !== 'Unknown') {
          if (type === 'pickup') {
            setQuoteForm(f => ({ ...f, pickup_city: data.city, pickup_state: data.state }));
            setPickupValid(true);
          } else {
            setQuoteForm(f => ({ ...f, delivery_city: data.city, delivery_state: data.state }));
            setDeliveryValid(true);
          }
        } else {
          if (type === 'pickup') { setPickupValid(false); setQuoteForm(f => ({ ...f, pickup_city: '', pickup_state: '' })); }
          else { setDeliveryValid(false); setQuoteForm(f => ({ ...f, delivery_city: '', delivery_state: '' })); }
        }
      } catch {
        if (type === 'pickup') setPickupValid(false); else setDeliveryValid(false);
      }
    } else {
      if (type === 'pickup') { setPickupValid(null); setQuoteForm(f => ({ ...f, pickup_city: '', pickup_state: '' })); }
      else { setDeliveryValid(null); setQuoteForm(f => ({ ...f, delivery_city: '', delivery_state: '' })); }
    }
  };

  const handleQuotePincode = (value, type) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 6);
    if (type === 'pickup') {
      setQuoteForm(f => ({ ...f, pickup_pincode: cleaned }));
    } else {
      setQuoteForm(f => ({ ...f, delivery_pincode: cleaned }));
    }
    if (cleaned.length === 6) fetchPincode(cleaned, type);
  };

  const handleTracking = async (e) => {
    e.preventDefault();
    if (!trackingId.trim()) return;
    setIsTracking(true);
    setTrackingError('');
    setTrackingResult(null);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/tracking/${trackingId}`);
      setTrackingResult(data);
    } catch (error) {
      setTrackingError(error.response?.data?.detail || 'Tracking ID not found');
    } finally {
      setIsTracking(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!quoteForm.name || !quoteForm.email || !quoteForm.phone) { alert('Please fill name, email and phone'); return; }
    if (quoteForm.pickup_pincode && pickupValid === false) { alert('Invalid pickup pincode'); return; }
    if (quoteForm.delivery_pincode && deliveryValid === false) { alert('Invalid delivery pincode'); return; }
    setQuoteLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/inquiry`, quoteForm);
      setShowQuoteForm(false);
      setShowSuccessModal(true);
      setQuoteForm(INITIAL_QUOTE_FORM);
      setPickupValid(null);
      setDeliveryValid(null);
    } catch {
      alert('Failed to submit. Please try again.');
    } finally {
      setQuoteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-[#0B0F19] dark:to-[#050505] transition-colors duration-500">
      {/* Header */}
      <header className="fixed w-full top-0 z-50 bg-white/95 dark:bg-black/95 backdrop-blur-xl border-b border-slate-200 dark:border-white/10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-5 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <img src={LOGO_URL} alt="Tridev Logistics" className="h-10 sm:h-14 w-auto drop-shadow-lg" />
            <div className="hidden sm:block">
              <span className="font-outfit text-2xl font-black bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent block">TRIDEV LOGISTICS</span>
              <p className="text-xs text-slate-600 dark:text-slate-400 font-medium tracking-wide">Trust · Time · Transportation</p>
            </div>
          </div>
          <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
            <Link to="/" className="text-slate-700 dark:text-slate-300 hover:text-amber-600 dark:hover:text-amber-500 font-medium text-sm transition-colors">Home</Link>
            <a href="#services" className="text-slate-700 dark:text-slate-300 hover:text-amber-600 dark:hover:text-amber-500 font-medium text-sm transition-colors">Services</a>
            <a href="#how-it-works" className="text-slate-700 dark:text-slate-300 hover:text-amber-600 dark:hover:text-amber-500 font-medium text-sm transition-colors">Process</a>
            <a href="#contact" className="text-slate-700 dark:text-slate-300 hover:text-amber-600 dark:hover:text-amber-500 font-medium text-sm transition-colors">Contact</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={toggleTheme} className="p-2 sm:p-2.5 rounded-xl bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 transition-all" data-testid="theme-toggle-btn">
              {theme === 'dark' ? <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" /> : <Moon className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700" />}
            </button>
            <button onClick={() => goTo('/login?type=customer')} className="px-3 sm:px-5 py-2 sm:py-2.5 bg-slate-100 dark:bg-white/5 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-white/10 rounded-xl font-semibold text-xs sm:text-sm transition-all" data-testid="customer-login-btn">Customer</button>
            <button onClick={() => goTo('/login?type=admin')} className="px-3 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 rounded-xl font-bold text-xs sm:text-sm shadow-lg shadow-amber-500/30 transition-all" data-testid="admin-login-btn">Admin</button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-24 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={(cms?.hero?.image_url) || HERO_IMAGE} alt="Modern Logistics Truck" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent dark:from-black/95 dark:via-black/80"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full">
              <Award className="w-4 h-4 text-amber-500" />
              <span className="text-amber-500 font-semibold text-sm">{cms?.hero?.badge || 'Trusted by 1000+ Businesses'}</span>
            </div>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-7xl font-black text-white leading-[1.1] tracking-tight">
              {cms?.hero?.title_line1 || 'Fast. Reliable.'}
              <span className="block bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent mt-2">{cms?.hero?.title_line2 || 'Nationwide Logistics'}</span>
            </h1>
            <p className="text-base sm:text-xl text-slate-300 leading-relaxed max-w-xl font-light">{cms?.hero?.description || 'Powering your supply chain with precision, reliability, and cutting-edge technology. Your trusted logistics partner across India.'}</p>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <button onClick={() => setShowQuoteForm(true)} className="group px-5 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 rounded-xl font-bold text-sm sm:text-lg flex items-center gap-2 shadow-2xl shadow-amber-500/30 transition-all">
                Get Free Quote<ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => goTo('/login?type=customer')} className="px-5 sm:px-8 py-3 sm:py-4 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-xl font-semibold text-sm sm:text-lg transition-all" data-testid="get-started-btn">Customer Login</button>
              <button onClick={() => document.getElementById('tracking-section')?.scrollIntoView({ behavior: 'smooth' })} className="px-5 sm:px-8 py-3 sm:py-4 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-xl font-semibold text-sm sm:text-lg transition-all" data-testid="track-shipment-btn">Track Shipment</button>
            </div>
            <div className="flex flex-wrap items-center gap-4 sm:gap-8 pt-6 sm:pt-8 border-t border-white/10">
              <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-amber-500" /><span className="text-white text-sm font-medium">100% Secure</span></div>
              <div className="flex items-center gap-2"><Clock className="w-5 h-5 text-amber-500" /><span className="text-white text-sm font-medium">24/7 Support</span></div>
              <div className="flex items-center gap-2"><Users className="w-5 h-5 text-amber-500" /><span className="text-white text-sm font-medium">1000+ Clients</span></div>
            </div>
          </div>

          {/* Tracking Card */}
          <div className="bg-white/10 dark:bg-black/40 backdrop-blur-2xl border border-white/20 rounded-3xl p-10 shadow-2xl" data-testid="tracking-widget">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-amber-500/10 rounded-xl"><MapPin className="w-7 h-7 text-amber-500" /></div>
              <h3 className="font-outfit text-2xl font-bold text-white">Track Your Shipment</h3>
            </div>
            <form onSubmit={handleTracking} className="space-y-5">
              <input type="text" placeholder="Enter Tracking ID (e.g., TDL123456)" value={trackingId} onChange={(e) => setTrackingId(e.target.value)} className="w-full px-5 py-4 bg-white/10 border-2 border-white/20 text-white rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-500/50 outline-none font-medium placeholder:text-white/50 transition-all" data-testid="tracking-input" />
              <button type="submit" disabled={isTracking} className="w-full px-5 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 rounded-xl font-bold text-lg disabled:opacity-50 shadow-lg shadow-amber-500/30 transition-all" data-testid="track-btn">
                {isTracking ? 'Tracking...' : 'Track Now'}
              </button>
            </form>
            {trackingError && (
              <div className="mt-5 p-4 bg-red-500/10 border border-red-500/30 rounded-xl" data-testid="tracking-error">
                <p className="text-red-400 text-sm font-medium">{trackingError}</p>
              </div>
            )}
            {trackingResult && (
              <PublicTrackingResult data={trackingResult} onClose={() => setTrackingResult(null)} />
            )}
          </div>
        </div>
      </section>

      <main>
        <LogisticsGlobe3D />
        <ServicesSection />
        <FleetSection />
        <CoverageSection />
        <ContactTeamSection />
        <AnimatedRouteSection />
        <StatsSection />
        <ProcessSection />
        <WhyChooseUsSection />
      </main>
      <HomeFooter />

      <QuoteModal
        show={showQuoteForm}
        onClose={() => setShowQuoteForm(false)}
        form={quoteForm}
        setForm={setQuoteForm}
        onPincodeChange={handleQuotePincode}
        pickupValid={pickupValid}
        deliveryValid={deliveryValid}
        onSubmit={handleQuoteSubmit}
        loading={quoteLoading}
      />
      <SuccessModal show={showSuccessModal} onClose={() => setShowSuccessModal(false)} />
    </div>
  );
};

const HomePageWrapper = ({ portalUrl }) => (
  <CMSProvider>
    <HomePagePremium portalUrl={portalUrl} />
  </CMSProvider>
);

export default HomePageWrapper;
